// ./src/components/features/Forms/FormFieldsEditModal/SlugFieldOptionsEditor.tsx
import React from "react";
import { Stack, Switch, Select, Text, NumberInput, Group } from "@mantine/core";
import { SlugFieldOptions } from "./fieldOptionsTypes";

export interface SlugFieldOptionsEditorProps {
  value: SlugFieldOptions;
  onChange: (options: SlugFieldOptions) => void;
  minValue: number | null;
  maxValue: number | null;
  onMinValueChange: (value: number | null) => void;
  onMaxValueChange: (value: number | null) => void;
  maxValueError?: string;
}

const SEPARATOR_OPTIONS = [
  { value: "hyphen", label: "Hyphens only (default)" },
  { value: "underscore", label: "Underscores only" },
  { value: "both", label: "Both hyphens and underscores" },
];

export const SlugFieldOptionsEditor: React.FC<SlugFieldOptionsEditorProps> = ({
  value,
  onChange,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  maxValueError,
}) => {
  const handleChange = (field: keyof SlugFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  // Generate example based on current settings
  const getExampleSlug = () => {
    const sep = value.separator ?? "hyphen";
    const separator = sep === "underscore" ? "_" : "-";
    let example = `my${separator}awesome`;
    if (value.allowNumbers !== false) {
      example += `${separator}post${separator}123`;
    } else {
      example += `${separator}post`;
    }
    return example;
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Validates URL-friendly slugs (lowercase letters, numbers, and
        separators).
      </Text>

      {/* Length Requirements */}
      <Group grow>
        <NumberInput
          label="Min Length"
          placeholder="1"
          value={minValue ?? ""}
          onChange={(val) => onMinValueChange(val === "" ? null : Number(val))}
          min={1}
          max={100}
          size="sm"
        />
        <NumberInput
          label="Max Length"
          placeholder="No max"
          value={maxValue ?? ""}
          onChange={(val) => onMaxValueChange(val === "" ? null : Number(val))}
          error={maxValueError}
          min={minValue ?? 1}
          max={500}
          size="sm"
        />
      </Group>

      {/* Separator Options */}
      <Select
        label="Separator character"
        description="Which separator characters to allow"
        data={SEPARATOR_OPTIONS}
        value={value.separator ?? "hyphen"}
        onChange={(val) =>
          handleChange("separator", val === "hyphen" ? undefined : val)
        }
        size="sm"
      />

      <Switch
        label="Allow numbers"
        description="Permit digits 0-9 in the slug"
        checked={value.allowNumbers ?? true}
        onChange={(e) =>
          handleChange(
            "allowNumbers",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      <Switch
        label="Trim separators"
        description="Disallow leading/trailing separators"
        checked={value.trimSeparators ?? true}
        onChange={(e) =>
          handleChange(
            "trimSeparators",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      <Switch
        label="Disallow consecutive separators"
        description="Prevent patterns like my--slug or my__slug"
        checked={value.disallowConsecutiveSeparators ?? true}
        onChange={(e) =>
          handleChange(
            "disallowConsecutiveSeparators",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      {/* Example */}
      <Text size="xs" c="dimmed">
        Example valid slug:{" "}
        <Text span ff="monospace" fw={500}>
          {getExampleSlug()}
        </Text>
      </Text>
    </Stack>
  );
};
